import React, { useMemo } from 'react';
import type { ReceptionistSettingsPageId } from '../../receptionist-settings/types';
import { RECEPTIONIST_SETTINGS_NAV } from '../../receptionist-settings/config';

export interface ReceptionistSettingsSubNavProps {
  active: ReceptionistSettingsPageId;
  onChange: (id: ReceptionistSettingsPageId) => void;
  isDirty: boolean;
}

export function ReceptionistSettingsSubNav({ active, onChange, isDirty }: ReceptionistSettingsSubNavProps) {
  const groups = useMemo(() => {
    const g: Record<string, typeof RECEPTIONIST_SETTINGS_NAV> = {};
    RECEPTIONIST_SETTINGS_NAV.forEach((it) => {
      const group = g[it.group] ?? [];
      group.push(it);
      g[it.group] = group;
    });
    return g;
  }, []);

  return (
    <nav
      className="sticky top-0 flex min-h-full w-[244px] shrink-0 flex-col gap-[22px] self-stretch overflow-auto border-r border-[var(--cream-2)] bg-[var(--cream-0)] py-[26px] pb-5"
    >
      <div className="px-5">
        <div className="set-eyebrow mb-1.5">Settings</div>
        <h2 className="m-0 font-[var(--font-display)] text-[22px] font-medium leading-[1.05] tracking-[-0.025em] text-[var(--ink-0)]">
          AI <span className="set-serif text-[26px] font-normal tracking-[-0.015em]">receptionist</span>
        </h2>
      </div>

      {Object.entries(groups).map(([groupName, items]) => (
        <div key={groupName}>
          <div className="set-eyebrow px-5 pb-1.5 text-[9.5px] text-[var(--ink-3)]">{groupName}</div>
          <div className="flex flex-col">
            {items.map((it) => {
              const isActive = active === it.id;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => onChange(it.id)}
                  className={['set-subnav-btn', isActive ? 'set-subnav-btn-active' : ''].join(' ')}
                >
                  <span>{it.label}</span>
                  {isActive ? (
                    <span
                      className="h-1.5 w-1.5 shrink-0 rounded-[999px]"
                      style={{ background: 'var(--set-green)' }}
                    />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="mt-auto border-t border-[var(--cream-2)] px-5 pt-4">
        <div className="mb-1 flex items-center gap-2">
          <span
            className="h-[7px] w-[7px] shrink-0 rounded-[999px]"
            style={{ background: isDirty ? 'var(--set-warning)' : 'var(--set-green)' }}
          />
          <div className="text-[12px] font-semibold tracking-[-0.005em] text-[var(--ink-0)]">
            {isDirty ? 'Unsaved changes' : 'Live config'}
          </div>
        </div>
        <div className="text-[11px] leading-[1.5] tracking-[-0.005em] text-[var(--ink-2)]">
          {isDirty
            ? 'Save to apply your changes to live calls.'
            : 'Changes apply to live calls immediately on save.'}
        </div>
      </div>
    </nav>
  );
}
